<template>
	<div v-if="shouldShow" :class="['w-full py-5', backgroundClass]">
		<div
			class="container flex flex-col md:flex-row md:items-center justify-between gap-3"
		>
			<!-- Left: White card with content -->
			<div
				:class="[
					'w-full md:w-auto flex flex-wrap md:flex-nowrap items-start md:items-center gap-3 py-3 px-4 rounded-lg bg-white',
					cardBorderClass
				]"
			>
				<!-- Icon -->
				<div
					:class="[
						'flex items-center justify-center w-10 h-10 rounded-lg shrink-0',
						iconBackgroundClass
					]"
				>
					<IconSubscriptionStatus :variant="variant" :size="22" />
				</div>

				<!-- Text content -->
				<div class="flex-1 min-w-0">
					<p class="m-0 font-semibold text-sm text-gray-900">
						{{ statusTitle }}
					</p>
					<p class="m-0 text-[12px] text-gray-500 mt-0.5">
						{{ statusSubtitle }}
					</p>
				</div>

				<!-- Days counter with divider (trial and expiring only) -->
				<div
					v-if="showDaysCounter"
					class="flex flex-col items-center pl-4 ml-auto md:ml-1 md:border-l md:border-gray-200 shrink-0"
				>
					<span
						class="text-2xl font-bold leading-none"
						:style="{ color: variantColor }"
						>{{ daysRemaining }}</span
					>
					<span class="text-xs text-gray-500 mt-0.5">{{
						daysLeftText
					}}</span>
				</div>
			</div>

			<!-- Right: Action button + Close -->
			<div class="w-full md:w-auto flex flex-wrap items-center justify-end gap-3 md:gap-8">
				<Button
					v-if="canShowBillingAction && usesBillingPortal"
					type="secondary"
					class="w-full sm:w-auto"
					:loading="isLoadingPortal"
					@click="openBillingPortal"
				>
					{{ actionText }}
				</Button>
				<Button
					v-else-if="canShowBillingAction && actionLink"
					type="secondary"
					class="w-full sm:w-auto"
					@click="navigateTo(actionLink)"
				>
					{{ actionText }}
				</Button>

				<!-- Close button (active only) -->
				<button
					v-if="canDismiss"
					type="button"
					class="p-1 text-gray-500 hover:text-gray-700 transition-colors"
					:aria-label="$t('general.close') || 'Close'"
					@click="dismiss"
				>
					<Icon :icon="['fal', 'times']" class="text-xl" />
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import IconSubscriptionStatus from '~/components/Icons/IconSubscriptionStatus.vue'
import Icon from '~/components/ui/common/Icon'
import Button from '~/components/ui/common/Button'
import { PRICING_TIERS } from '~/constants/pricing'
import { formatTrialDate } from '~/utils/trialDate'
import {
	hasExpiredPaidSubscription,
	hasFailedPaymentStatus,
	hasOrganizationPaidAccess,
	isActivePaidSubscription,
	isPaidThroughSubscription
} from '~/utils/subscriptionAccess'

const CHECKOUT_BASELINE_STORAGE_KEY = 'apropos_subscription_checkout_baseline'
const CHECKOUT_BASELINE_TTL_MS = 60 * 60 * 1000 // 1 hour

export default {
	name: 'SubscriptionBanner',
	components: {
		IconSubscriptionStatus,
		Icon,
		Button
	},
	data() {
		return {
			isDismissed: false,
			isLoadingPortal: false,
			wasCheckoutSuccess: false
		}
	},
	computed: {
		// Check if user just completed a subscription (success=true in URL)
		justCompletedSubscription() {
			return this.wasCheckoutSuccess
		},

		shouldShow() {
			if (!this.$org) return false
			if (this.isDismissed) return false

			// For active subscriptions: only show right after completing a new subscription
			if (this.variant === 'active') {
				return this.justCompletedSubscription
			}

			return this.variant !== null
		},

		canShowBillingAction() {
			return this.$can('manage organization billing')
		},

		hasPaidSubscription() {
			return hasOrganizationPaidAccess(this.$org)
		},

		isSubscriptionCancelled() {
			// Customer-cancelled paid subscriptions remain serviceable until ends_at.
			// Cashier/Stripe data can represent that as active or canceled/cancelled.
			const subscription = this.$org?.subscription
			if (!subscription) return false

			return isPaidThroughSubscription(subscription)
		},

		isSubscriptionExpiring() {
			return this.isSubscriptionCancelled
		},

		isSubscriptionExpired() {
			// Match backend and route middleware: incomplete setup and paused
			// subscriptions are hard blocks, even if an older row is paid-through.
			if (['incomplete', 'paused'].includes(this.$org?.payment_status)) {
				return true
			}

			if (
				this.$org?.is_in_trial === true &&
				this.$org?.trial_expired !== true
			) {
				return false
			}

			// The API owns the final access decision. If it reports paid-through
			// access, do not downgrade the banner from stale subscription details.
			if (this.hasPaidSubscription) return false

			const subscription = this.$org?.subscription
			if (!subscription) return false

			// Primary source: Stripe/Cashier subscription status
			if (hasExpiredPaidSubscription(subscription)) {
				return true
			}

			// Fallback: org-level payment_status can be updated even when subscription status isn't
			if (
				!subscription.is_free &&
				hasFailedPaymentStatus(this.$org?.payment_status)
			) {
				return true
			}

			// In some cases (or during manual DB updates), ends_at can be in the past while
			// stripe_status still reports active. Treat that as expired.
			if (subscription.ends_at) {
				const endsAt = new Date(subscription.ends_at)
				if (!Number.isNaN(endsAt.getTime()) && endsAt <= new Date()) {
					return true
				}
			}

			return false
		},

		variant() {
			if (!this.$org) return null

			if (this.isSubscriptionExpired) {
				return 'expired'
			}

			if (this.hasPaidSubscription && this.isSubscriptionExpiring) {
				return 'expiring'
			}

			if (this.hasPaidSubscription) {
				return 'active'
			}

			if (this.$org.is_in_trial) {
				const daysRemaining = Number(this.$org.days_remaining_in_trial)
				if (this.$org.trial_ends_on && Number.isFinite(daysRemaining) && daysRemaining <= 3) {
					return 'trial_expiring'
				}
				return 'trial'
			}

			if (this.$org.trial_expired) {
				return 'trial_ended'
			}

			if (['cancelled', 'canceled', 'past_due', 'unpaid', 'incomplete', 'paused'].includes(this.$org.payment_status)) {
				return 'expired'
			}

			return null
		},

		backgroundClass() {
			switch (this.variant) {
				case 'active':
					return 'bg-[#E6F4EC]'
				case 'expiring':
				case 'trial_expiring':
					return 'bg-[#FEF2C7]'
				case 'expired':
				case 'trial_ended':
					return 'bg-[#FEE6E1]'
				case 'trial':
					return 'bg-[#E6F0F4]'
				default:
					return ''
			}
		},

		cardBorderClass() {
			switch (this.variant) {
				case 'expiring':
				case 'trial_expiring':
					return 'border border-[#E3E3E3] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)] rounded-[10px]'
				case 'trial':
					return 'border border-[#E3E3E3]'
				default:
					return ''
			}
		},

		iconBackgroundClass() {
			switch (this.variant) {
				case 'expiring':
				case 'trial_expiring':
					return 'bg-[#F2E8C7]'
				default:
					return this.backgroundClass
			}
		},

		variantColor() {
			switch (this.variant) {
				case 'trial':
					return '#1E3B4C'
				case 'trial_ended':
				case 'expired':
				case 'payment_failed':
					return '#531306'
				case 'active':
					return '#164028'
				case 'expiring':
				case 'trial_expiring':
					return '#623400'
				default:
					return '#1E3B4C'
			}
		},

		statusTitle() {
			switch (this.variant) {
				case 'active':
					return this.$te('subscription.banner.active_title')
						? this.$t('subscription.banner.active_title')
						: 'Your subscription is active'
				case 'expiring':
					return this.$te('subscription.banner.expiring_title')
						? this.$t('subscription.banner.expiring_title')
						: 'Your subscription is ending soon'
				case 'trial_expiring':
					return this.$te('subscription.banner.trial_expiring_title')
						? this.$t('subscription.banner.trial_expiring_title')
						: 'Trial expiring'
				case 'expired':
					return this.$te('subscription.banner.expired_title')
						? this.$t('subscription.banner.expired_title')
						: 'Your subscription has ended'
				case 'trial_ended':
					return this.$te('subscription.banner.trial_ended_title')
						? this.$t('subscription.banner.trial_ended_title')
						: 'Your free trial has ended'
				case 'trial':
					return this.$te('subscription.banner.trial_title')
						? this.$t('subscription.banner.trial_title')
						: 'Your free trial is active'
				default:
					return ''
			}
		},

		statusSubtitle() {
			switch (this.variant) {
				case 'active':
					return this.activeSubscriptionDetails
				case 'expiring':
					return this.expiringDetails
				case 'expired':
					return this.$te('subscription.banner.expired_subtitle')
						? this.$t('subscription.banner.expired_subtitle')
						: 'Your access is paused — reactivate to restore access'
				case 'trial_ended':
					return this.$te('subscription.banner.trial_ended_subtitle')
						? this.$t('subscription.banner.trial_ended_subtitle')
						: 'Upgrade to regain access to all features'
				case 'trial_expiring':
				case 'trial':
					return this.trialDetails
				default:
					return ''
			}
		},

		activeSubscriptionDetails() {
			const enjoyText = this.$te('subscription.banner.enjoy_features')
				? this.$t('subscription.banner.enjoy_features')
				: 'Enjoy all the features of the PRO Plan'

			const parts = [enjoyText]

			if (this.$org?.company_size) {
				const tier = PRICING_TIERS[this.$org.company_size]
				if (tier) {
					parts.push(tier.label)
				}
			}

			const nextDate = this.formatNextBillingDate()
			if (nextDate) {
				const nextLabel = this.$te('subscription.banner.next')
					? this.$t('subscription.banner.next')
					: 'Next'
				parts.push(`${nextLabel}: ${nextDate}`)
			}

			return parts.join(' · ')
		},

		expiringDetails() {
			const endDate = this.formatNextBillingDate()
			if (!endDate) {
				return this.$te('subscription.banner.expiring_subtitle')
					? this.$t('subscription.banner.expiring_subtitle')
					: 'Renew to avoid losing access'
			}

			return this.$te('subscription.banner.expiring_subtitle_date')
				? this.$t('subscription.banner.expiring_subtitle_date', {
						date: endDate
				  })
				: `Renew by ${endDate} to avoid losing access`
		},

		trialDetails() {
			if (!this.$org?.trial_ends_on) {
				return this.$te('subscription.banner.trial_subtitle')
					? this.$t('subscription.banner.trial_subtitle')
					: 'Explore all Pro features'
			}

			const endDate = formatTrialDate(
				this.$org.trial_ends_on,
				this.$i18n?.locale || 'en'
			)
			return this.$te('subscription.banner.trial_until')
				? this.$t('subscription.banner.trial_until', { date: endDate })
				: `Explore all Pro features until ${endDate}`
		},

		daysRemaining() {
			if (!this.$org) return 0

			if (this.variant === 'trial' || this.variant === 'trial_expiring') {
				return this.$org.days_remaining_in_trial || 0
			}

			if (this.variant === 'expiring') {
				const subscription = this.$org?.subscription

				// Use ends_at (set by Cashier webhook when cancelled)
				if (subscription?.ends_at) {
					const endDate = new Date(subscription.ends_at)
					const now = new Date()
					return Math.max(
						0,
						Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))
					)
				}
			}

			return 0
		},

		showDaysCounter() {
			return (
				this.variant === 'trial' ||
				this.variant === 'trial_expiring' ||
				this.variant === 'expiring'
			)
		},

		daysLeftText() {
			return this.$te('subscription.banner.days_left')
				? this.$t('subscription.banner.days_left')
				: 'days left'
		},

		actionText() {
			switch (this.variant) {
				case 'active':
					return this.$te('subscription.banner.manage')
						? this.$t('subscription.banner.manage')
						: 'Manage subscription'
				case 'expiring':
					return this.$te('subscription.banner.renew')
						? this.$t('subscription.banner.renew')
						: 'Renew subscription'
				case 'expired':
					return this.$te('subscription.banner.reactivate')
						? this.$t('subscription.banner.reactivate')
						: 'Reactivate subscription'
				case 'trial_ended':
					return this.$te('subscription.banner.upgrade_now')
						? this.$t('subscription.banner.upgrade_now')
						: 'Upgrade now'
				case 'trial_expiring':
				case 'trial':
					return this.$te('subscription.banner.choose_plan')
						? this.$t('subscription.banner.choose_plan')
						: 'Choose a plan'
				default:
					return ''
			}
		},

		actionLink() {
			// Active subscription uses billing portal (handled by method)
			if (this.variant === 'active') {
				return null // Will use openBillingPortal method
			}
			// Default: navigate to pricing page
			return '/organization/pricing'
		},

		usesBillingPortal() {
			// Use Stripe billing portal for active and cancelled subscriptions
			// (cancelled shows as 'expiring' variant but needs billing portal to renew)
			if (this.variant === 'active') return true
			if (this.variant === 'expiring' && this.isSubscriptionCancelled)
				return true
			return false
		},

		canDismiss() {
			// Only active subscription banner (shown after completing subscription) can be dismissed
			return this.variant === 'active'
		}
	},
	mounted() {
		this.captureCheckoutSignals()
	},
	watch: {
		'$route.query.success'(value) {
			if (value === 'true') {
				this.wasCheckoutSuccess = true
				this.clearCheckoutBaseline()
			}
		},
		'$org.is_paid'() {
			this.captureCheckoutSignals()
		},
		'$org.has_paid_access'() {
			this.captureCheckoutSignals()
		},
		'$org.company_size'() {
			this.captureCheckoutSignals()
		},
		'$org.subscription': {
			handler() {
				this.captureCheckoutSignals()
			},
			deep: true
		}
	},
	methods: {
		getCheckoutBaseline() {
			if (typeof window === 'undefined') return null
			try {
				const raw = window.sessionStorage.getItem(
					CHECKOUT_BASELINE_STORAGE_KEY
				)
				if (!raw) return null
				return JSON.parse(raw)
			} catch (e) {
				return null
			}
		},

		clearCheckoutBaseline() {
			if (typeof window === 'undefined') return
			try {
				window.sessionStorage.removeItem(CHECKOUT_BASELINE_STORAGE_KEY)
			} catch (e) {
				// ignore
			}
		},

		persistCheckoutBaseline() {
			if (typeof window === 'undefined') return
			try {
				const baseline = {
					startedAt: Date.now(),
					hasPaidSubscription: hasOrganizationPaidAccess(this.$org),
					hadActivePaidSubscription: isActivePaidSubscription(
						this.$org?.subscription
					),
					stripe_price: this.$org?.subscription?.stripe_price || null,
					stripe_status:
						this.$org?.subscription?.stripe_status || null,
					ends_at: this.$org?.subscription?.ends_at || null,
					company_size: this.$org?.company_size || null
				}
				window.sessionStorage.setItem(
					CHECKOUT_BASELINE_STORAGE_KEY,
					JSON.stringify(baseline)
				)
			} catch (e) {
				// ignore
			}
		},

		captureCheckoutSignals() {
			if (!this.$org) return

			if (this.$route?.query?.success === 'true') {
				this.wasCheckoutSuccess = true
				this.clearCheckoutBaseline()
				return
			}

			const baseline = this.getCheckoutBaseline()
			if (!baseline) return

			const startedAt = Number(baseline.startedAt)
			if (
				!Number.isFinite(startedAt) ||
				Date.now() - startedAt > CHECKOUT_BASELINE_TTL_MS
			) {
				this.clearCheckoutBaseline()
				return
			}

			if (this.variant !== 'active') return

			const currentPrice = this.$org?.subscription?.stripe_price || null
			const currentCompanySize = this.$org?.company_size || null
			const currentHasPaid = this.hasPaidSubscription
			const currentHasActivePaid = isActivePaidSubscription(
				this.$org?.subscription
			)

			const becamePaid =
				!baseline.hasPaidSubscription && currentHasPaid === true
			const renewedSubscription =
				baseline.hasPaidSubscription === true &&
				baseline.hadActivePaidSubscription === false &&
				currentHasActivePaid === true
			const priceChanged =
				baseline.stripe_price &&
				currentPrice &&
				baseline.stripe_price !== currentPrice
			const companySizeChanged =
				baseline.company_size &&
				currentCompanySize &&
				baseline.company_size !== currentCompanySize

			if (
				becamePaid ||
				renewedSubscription ||
				priceChanged ||
				companySizeChanged
			) {
				this.wasCheckoutSuccess = true
				this.clearCheckoutBaseline()
			}
		},
		dismiss() {
			if (!this.canDismiss) return
			// Just hide it - no localStorage needed, it only shows after completing subscription
			this.isDismissed = true
		},

		navigateTo(path) {
			this.$router.push(path)
		},

		async openBillingPortal() {
			if (this.isLoadingPortal) return

			this.isLoadingPortal = true
			try {
				const url = await this.$axios.$get(
					'/payment/get-billing-portal-url'
				)
				if (url) {
					this.persistCheckoutBaseline()
					window.location.href = url
				} else {
					// Fallback: Check if this is a local subscription (no real Stripe portal)
					// In local dev, offer to manage via pricing page
					this.$router.push('/organization/pricing')
				}
			} catch (error) {
				console.error('Failed to get billing portal URL:', error)
				// Fallback to pricing page on error
				this.$router.push('/organization/pricing')
			} finally {
				this.isLoadingPortal = false
			}
		},

		formatDate(dateString) {
			if (!dateString) return ''
			const date = new Date(dateString)
			const options = { day: 'numeric', month: 'short', year: 'numeric' }
			return date.toLocaleDateString(this.$i18n?.locale || 'en', options)
		},

		formatNextBillingDate() {
			if (!this.$org?.subscription) return null

			// Use ends_at for canceled subscriptions
			if (this.$org.subscription.ends_at) {
				return this.formatDate(this.$org.subscription.ends_at)
			}

			// Calculate from created_at + billing period (approximate)
			const createdAt = this.$org.subscription.created_at
			if (!createdAt) return null

			const date = new Date(createdAt)
			date.setMonth(date.getMonth() + 1)
			return this.formatDate(date.toISOString())
		}
	}
}
</script>
